import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import './ResetPasswordPage.css';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      setError('Ugyldig tilbakestillings-lenke. Token mangler.');
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Ugyldig tilbakestillings-lenke');
      return;
    }

    if (!newPassword) {
      setError('Vennligst oppgi nytt passord');
      return;
    }

    if (newPassword.length < 6) {
      setError('Passordet må være minst 6 tegn langt');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passordene stemmer ikke overens');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        token,
        newPassword,
      });

      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/logg-inn');
      }, 3000);
    } catch (err: any) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Kunne ikke tilbakestille passordet. Lenken kan være utløpt.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-password-page">
      <div className="reset-password-container">
        <div className="reset-password-card">
          <h1>Tilbakestill passord</h1>

          {success ? (
            <div className="success-container">
              <div className="success-message">
                <h2>✅ Passordet er tilbakestilt!</h2>
                <p>
                  Passordet ditt er nå oppdatert. Du blir sendt til innloggingssiden om noen sekunder.
                </p>
              </div>
              <Link to="/logg-inn" className="btn btn-primary btn-full">
                Gå til innlogging
              </Link>
            </div>
          ) : (
            <>
              {error && <div className="error-message">{error}</div>}

              {!token ? (
                <div className="error-container">
                  <p>Ugyldig tilbakestillings-lenke. Vennligst be om en ny lenke.</p>
                  <Link to="/glemt-passord" className="btn btn-primary btn-full">
                    Be om ny lenke
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="reset-password-form">
                  <p className="subtitle">
                    Oppgi ditt nye passord nedenfor. Passordet må være minst 6 tegn langt.
                  </p>

                  <div className="form-group">
                    <label htmlFor="newPassword" className="form-label">
                      Nytt passord
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      className="form-input"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Oppgi nytt passord"
                      disabled={loading}
                      autoFocus
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirmPassword" className="form-label">
                      Bekreft passord
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      className="form-input"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Gjenta nytt passord"
                      disabled={loading}
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary btn-full"
                    disabled={loading}
                  >
                    {loading && (
                      <span className="spinner" role="status" aria-label="Laster">
                        <svg className="spinner-icon" viewBox="0 0 50 50">
                          <circle
                            className="spinner-path"
                            cx="25"
                            cy="25"
                            r="20"
                            fill="none"
                            strokeWidth="4"
                          ></circle>
                        </svg>
                      </span>
                    )}
                    {loading ? 'Tilbakestiller...' : 'Tilbakestill passord'}
                  </button>
                </form>
              )}

              <div className="reset-password-footer">
                <p>
                  Husker du passordet?{' '}
                  <Link to="/logg-inn" className="link">
                    Logg inn her
                  </Link>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
